import { Box, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import { AutoAwesome } from '@mui/icons-material';
import { GENESIS_COLORS } from './types';
import { useIsFirstNote } from './useGenesisEvent';

interface FirstNoteBadgeProps {
  noteId?: string;
  /** Position of the badge */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Size variant */
  size?: 'small' | 'medium';
}

/**
 * FirstNoteBadge Component
 *
 * A permanent visual marker that appears on the user's first note.
 * Shows a sparkle badge with "FIRST" text and golden styling.
 *
 * Used to mark the genesis note with permanent visual distinction.
 */
const FirstNoteBadge = ({ noteId, position = 'top-right', size = 'small' }: FirstNoteBadgeProps) => {
  const { isFirstNote, loading } = useIsFirstNote(noteId);

  if (loading || !isFirstNote) {
    return null;
  }

  const positionStyles = {
    'top-left': { top: 8, left: 8 },
    'top-right': { top: 8, right: 8 },
    'bottom-left': { bottom: 8, left: 8 },
    'bottom-right': { bottom: 8, right: 8 },
  };

  const sizeStyles = {
    small: {
      padding: '4px 8px',
      fontSize: '0.65rem',
      iconSize: 12,
      gap: 0.5,
    },
    medium: {
      padding: '6px 12px',
      fontSize: '0.75rem',
      iconSize: 16,
      gap: 1,
    },
  };

  const currentSize = sizeStyles[size];

  return (
    <Tooltip title="Your origin story - Your very first note!" arrow placement="top">
      <Box
        component={motion.div}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        sx={{
          position: 'absolute',
          ...positionStyles[position],
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: currentSize.gap,
          padding: currentSize.padding,
          borderRadius: '20px',
          background: `linear-gradient(135deg, ${GENESIS_COLORS.accent} 0%, #FFD700 100%)`,
          boxShadow: `0 2px 8px rgba(245, 158, 11, 0.4), 0 0 16px rgba(245, 158, 11, 0.2)`,
          border: '1px solid rgba(255, 255, 255, 0.3)',
          cursor: 'default',
        }}
      >
        <Box
          component={motion.div}
          animate={{
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'easeInOut',
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <AutoAwesome sx={{ fontSize: currentSize.iconSize, color: 'white' }} />
        </Box>
        <Box
          component="span"
          sx={{
            color: 'white',
            fontWeight: 700,
            fontSize: currentSize.fontSize,
            letterSpacing: '0.1em',
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        >
          FIRST
        </Box>
      </Box>
    </Tooltip>
  );
};

/**
 * FirstNoteBorder Component
 *
 * A golden/holographic border effect for the first note's thumbnail.
 * Wraps around the video thumbnail to give it special treatment.
 */
export const FirstNoteBorder = ({ noteId, children }: { noteId?: string; children: React.ReactNode }) => {
  const { isFirstNote, loading } = useIsFirstNote(noteId);

  if (loading || !isFirstNote) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '12px',
        padding: '3px',
        background: `linear-gradient(135deg, ${GENESIS_COLORS.accent}, ${GENESIS_COLORS.primary}, ${GENESIS_COLORS.secondary}, ${GENESIS_COLORS.accent})`,
        backgroundSize: '300% 300%',
        animation: 'gradient-shift 4s ease infinite',
        boxShadow: `0 0 20px ${GENESIS_COLORS.glow}, 0 0 40px rgba(245, 158, 11, 0.2)`,
        '@keyframes gradient-shift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      }}
    >
      <Box
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          background: 'black',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default FirstNoteBadge;
